// Regular fund deposits + the value projection (finance table, per-user).
//
// Stored rows — `fund-deposits#<uuid>`, a deposit PLAN: `{ from, amounts: {
// [productId]: { amount (AUD minor units), currency: 'AUD' } }, period }`.
// A plan applies from its `from` date until the next plan starts; every
// amount 0 (or none) = paused. Deposits land on each PAYDAY by default — the
// pay schedule of the job you're in that day (between jobs / after the last
// one, the previous job's; before any job, monthly from the plan's date) —
// or on the plan's own `period` (weekly / fortnightly / monthly, counted from
// its `from`). Used for the PROJECTION only: history = real transactions.
//
// Pure derivations: `scheduledDeposits` (the dated deposits a set of plans
// makes) and `projectFunds` (each fund grown at its own yearly rate plus its
// deposits, shaped like fund-returns.js `valueHistory` so the chart joins).

import { financeStore } from './store.js';
import { addDays, daysBetween, isoDate, monthLength, toYmd } from './fy.js';
import { money, readMoney } from './money.js';
import { normaliseSchedule, paydaysBetween } from './pay-schedule.js';
import { normaliseProductId } from './funds.js';

export const FUND_DEPOSITS_PREFIX = 'fund-deposits#';
/** A plan's own cadence (instead of each payday). */
export const DEPOSIT_PERIODS = Object.freeze(['weekly', 'fortnightly', 'monthly']);
/** The projection horizon, in years. */
export const PROJECTION_YEARS = 7;
const CURRENCY = 'AUD';

function randomUuid() {
  return crypto.randomUUID();
}

/** A plan's stored shape (zero amounts dropped; no amounts = paused). */
export function normaliseDepositPlan(p) {
  let from;
  try {
    from = isoDate(p?.from);
  } catch {
    from = isoDate(new Date());
  }
  const amounts = {};
  for (const [id, value] of Object.entries(p?.amounts ?? {})) {
    const productId = normaliseProductId(id);
    const m = readMoney(value, { min: 0, currencies: [CURRENCY] });
    if (productId && m && m.amount > 0) amounts[productId] = m;
  }
  return { from, amounts, period: DEPOSIT_PERIODS.includes(p?.period) ? p.period : null };
}

/** Whether a plan deposits nothing. */
export function isPausedPlan(plan) {
  return !Object.values(plan.amounts ?? {}).some((m) => m.amount > 0);
}

/** A plan's total per deposit (AUD minor units). */
export function planTotalCents(plan) {
  return Object.values(plan.amounts ?? {}).reduce((s, m) => s + m.amount, 0);
}

const withId = (row, normalised) => ({ itemId: row.itemId, ...normalised });

/** Every plan, oldest first. */
export async function listDepositPlans(doc, userId) {
  const rows = await financeStore.listItems(doc, userId, FUND_DEPOSITS_PREFIX);
  return sortPlans(rows.map((r) => withId(r, normaliseDepositPlan(r))));
}

export function sortPlans(plans) {
  return [...plans].sort((a, b) => a.from.localeCompare(b.from));
}

export async function createDepositPlan(doc, userId, plan, genId = randomUuid) {
  const stored = normaliseDepositPlan(plan);
  const itemId = `${FUND_DEPOSITS_PREFIX}${genId()}`;
  const row = await financeStore.addItem(doc, userId, FUND_DEPOSITS_PREFIX, stored, itemId);
  return withId(row, stored);
}

export async function updateDepositPlan(doc, userId, itemId, plan) {
  const stored = normaliseDepositPlan(plan);
  await financeStore.updateItem(doc, userId, itemId, stored);
  return { itemId, ...stored };
}

export function deleteDepositPlan(doc, userId, itemId) {
  return financeStore.deleteItem(doc, userId, itemId);
}

// ─── Cadence ─────────────────────────────────────────────────────────────────

/**
 * The pay-schedule segments deposits follow, ascending: `[{ start, schedule }]`
 * — each real (non-scenario) job's schedule from its start date until the next
 * job starts (so a gap or the time after the last job keeps the previous job's
 * cadence). Dates before the first segment have no pay schedule.
 */
export function paySegments(employments) {
  return employments
    .filter((e) => !e.scenario && e.pay)
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
    .map((e) => ({ start: e.startDate, schedule: e.pay }));
}

/** The pay schedule deposits follow on `date` (null = before any job). */
export function payScheduleOn(employments, date) {
  return paySegments(employments).findLast((s) => s.start <= date)?.schedule ?? null;
}

/**
 * The pay schedules a pay-day plan follows from its start until `until` (the
 * day before the next plan; null = open-ended), in order: `[{ from, schedule }]`
 * — `schedule` null = before any job (monthly from the plan's date). Empty for
 * a plan with its own period. The same rules `scheduledDeposits` uses.
 */
export function planCadences(plan, employments, until = null) {
  if (plan.period) return [];
  const segments = paySegments(employments);
  const out = [];
  if (!segments.length || plan.from < segments[0].start) {
    out.push({ from: plan.from, schedule: null });
  }
  segments.forEach((s, i) => {
    const next = segments[i + 1]?.start;
    if (next && next <= plan.from) return; // replaced before the plan starts
    if (until && s.start > until) return; // starts after the plan ends
    out.push({ from: s.start > plan.from ? s.start : plan.from, schedule: s.schedule });
  });
  return out;
}

/** The dates one plan deposits on between `from` and `to` (inclusive). */
function planDates(plan, segments, from, to) {
  if (from > to) return [];
  if (plan.period) {
    return paydaysBetween(
      normaliseSchedule({ frequency: plan.period, anchor: plan.from }),
      from,
      to,
    );
  }
  const out = [];
  // Before the first job: monthly from the plan's own date.
  const firstStart = segments[0]?.start ?? null;
  if (!firstStart || from < firstStart) {
    const dayBefore = firstStart ? addDays(firstStart, -1) : to;
    const monthly = normaliseSchedule({ frequency: 'monthly', anchor: plan.from });
    out.push(...paydaysBetween(monthly, from, dayBefore < to ? dayBefore : to));
  }
  segments.forEach((s, i) => {
    const next = segments[i + 1]?.start;
    const a = s.start > from ? s.start : from;
    const end = next ? addDays(next, -1) : to;
    const b = end < to ? end : to;
    out.push(...paydaysBetween(s.schedule, a, b));
  });
  return out;
}

/**
 * The dates a regular payment lands on between `from` and `to` (inclusive):
 * `cadence` = `{ from, period }` — its own `period` (DEPOSIT_PERIODS, counted
 * from `from`), or null = each payday of the job you're in (monthly from
 * `from` before any job). The cadence rules deposit plans use, for any other
 * regular payment (e.g. loan contributions).
 */
export function cadenceDates(cadence, employments, from, to) {
  return planDates(cadence, paySegments(employments), from, to);
}

/**
 * Every deposit the plans make from `from` to `to` (inclusive), date order:
 * `[{ date, productId, cents }]`. Each plan runs until the next one starts.
 */
export function scheduledDeposits(plans, employments, from, to) {
  const sorted = sortPlans(plans);
  const segments = paySegments(employments);
  const out = [];
  sorted.forEach((plan, i) => {
    if (isPausedPlan(plan)) return;
    const next = sorted[i + 1]?.from;
    const a = plan.from > from ? plan.from : from;
    const end = next ? addDays(next, -1) : to;
    const b = end < to ? end : to;
    for (const date of planDates(plan, segments, a, b)) {
      for (const [productId, m] of Object.entries(plan.amounts)) {
        out.push({ date, productId, cents: m.amount });
      }
    }
  });
  return out.sort((x, y) => x.date.localeCompare(y.date));
}

// ─── Projection ──────────────────────────────────────────────────────────────

const grow = (cents, rate, days) => cents * Math.pow(1 + rate, days / 365);

/**
 * Project each fund forward from `from` (today) for `years`: today's value
 * (`startCents`) and each deposit grow at the fund's yearly `rates` (a
 * fund's distributions are part of that growth). Sampled every `step` days
 * from `from` (included, so it joins the history) to the end. Funds with no
 * value and no deposits are left out.
 * @returns {{ dates, funds: [{ productId, cents }], netInCents, depositedCents }}
 *   `netInCents` continues `startNetInCents`; `depositedCents` = the
 *   projection's own deposits, in total.
 */
export function projectFunds({
  startCents,
  deposits,
  rates,
  from,
  years = PROJECTION_YEARS,
  startNetInCents = 0,
  step = 7,
}) {
  const to = yearsAfter(from, years);
  const ids = [
    ...new Set([
      ...[...startCents].filter(([, c]) => c > 0).map(([id]) => id),
      ...deposits.filter((d) => d.date > from && d.date <= to).map((d) => d.productId),
    ]),
  ];
  const dates = [];
  for (let d = from; d < to; d = addDays(d, step)) dates.push(d);
  dates.push(to);
  const inWindow = deposits.filter((d) => d.date > from && d.date <= to);
  const funds = ids.map((productId) => {
    const rate = rates.get(productId) ?? 0;
    const start = startCents.get(productId) ?? 0;
    const own = inWindow.filter((d) => d.productId === productId);
    const cents = dates.map((date) =>
      Math.round(
        grow(start, rate, daysBetween(from, date)) +
          own
            .filter((d) => d.date <= date)
            .reduce((s, d) => s + grow(d.cents, rate, daysBetween(d.date, date)), 0),
      ),
    );
    return { productId, cents };
  });
  const netInCents = dates.map(
    (date) =>
      startNetInCents + inWindow.filter((d) => d.date <= date).reduce((s, d) => s + d.cents, 0),
  );
  return {
    dates,
    funds,
    netInCents,
    depositedCents: inWindow.reduce((s, d) => s + d.cents, 0),
  };
}

/** `date` + whole `years` (29 Feb → 28 Feb in a non-leap year). */
export function yearsAfter(date, years) {
  const { y, m, d } = toYmd(date);
  return isoDate({ y: y + years, m, d: Math.min(d, monthLength(y + years, m)) });
}

/** A money value for a plan amount (AUD minor units). */
export const planAmount = (cents) => money(cents, CURRENCY);
